import {
  useDeleteProductCategory,
  useDeleteProductSubcategory,
  useProductCategories,
  useProductSubcategories,
  useUpdateProductCategory,
  useUpdateProductSubcategory,
} from '@/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Paragraph,
} from '@/components/atoms';
import type { ProductCategory, ProductSubcategory } from '@/types';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CategoryCreateForm } from './category-create-form';

interface DeleteTarget {
  type: 'category' | 'subcategory';
  id: number;
  name: string;
  categoryId?: number;
}

interface CategoryManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CategoryManagementModal = ({
  open,
  onOpenChange,
}: CategoryManagementModalProps) => {
  const [view, setView] = useState<'list' | 'create'>('list');

  useEffect(() => {
    if (!open) {
      setView('list');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {view === 'list' ? 'Manage categories' : 'Create category'}
          </DialogTitle>
          <DialogDescription>
            {view === 'list'
              ? 'View, edit, or delete your product categories and subcategories.'
              : 'Add a category and one or more subcategories for your products.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {view === 'list' ? (
            <CategoryList onCreate={() => setView('create')} />
          ) : (
            <CategoryCreateForm
              showBackButton
              onBack={() => setView('list')}
              onSuccess={() => setView('list')}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function CategoryList({ onCreate }: { onCreate: () => void }) {
  const { data: categories = [], isLoading } = useProductCategories();

  return (
    <div className="flex flex-col gap-4">
      <Button
        type="button"
        variant="outline"
        className="w-fit gap-1.5"
        onClick={onCreate}
      >
        <Plus className="size-4" />
        Create category
      </Button>

      {isLoading ? (
        <Paragraph className="text-sm text-muted-foreground py-6 text-center">
          Loading categories...
        </Paragraph>
      ) : categories.length === 0 ? (
        <Paragraph className="text-sm text-muted-foreground py-6 text-center">
          No categories yet. Create your first category to get started.
        </Paragraph>
      ) : (
        <div className="flex flex-col gap-2">
          {categories.map((category) => (
            <CategoryListItem key={category.id} category={category} />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryListItem({ category }: { category: ProductCategory }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(category.name);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [editingSubcategoryId, setEditingSubcategoryId] = useState<
    number | null
  >(null);
  const [subcategoryEditName, setSubcategoryEditName] = useState('');

  const { data: subcategories = [], isLoading } = useProductSubcategories(
    category.id,
    expanded
  );

  const { mutate: updateCategory, isPending: isUpdatingCategory } =
    useUpdateProductCategory(() => setEditing(false));
  const { mutate: deleteCategory, isPending: isDeletingCategory } =
    useDeleteProductCategory(() => setDeleteTarget(null));
  const { mutate: updateSubcategory, isPending: isUpdatingSubcategory } =
    useUpdateProductSubcategory(category.id, () => {
      setEditingSubcategoryId(null);
      setSubcategoryEditName('');
    });
  const { mutate: deleteSubcategory, isPending: isDeletingSubcategory } =
    useDeleteProductSubcategory(category.id, () => setDeleteTarget(null));

  useEffect(() => {
    setEditName(category.name);
  }, [category.name]);

  const handleSaveCategory = () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === category.name) {
      setEditing(false);
      setEditName(category.name);
      return;
    }
    updateCategory({ id: category.id, name: trimmed });
  };

  const handleSaveSubcategory = (subcategory: ProductSubcategory) => {
    const trimmed = subcategoryEditName.trim();
    if (!trimmed || trimmed === subcategory.name) {
      setEditingSubcategoryId(null);
      setSubcategoryEditName('');
      return;
    }
    updateSubcategory({ id: subcategory.id, name: trimmed });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'category') {
      deleteCategory(deleteTarget.id);
      return;
    }
    deleteSubcategory(deleteTarget.id);
  };

  const isDeleting = isDeletingCategory || isDeletingSubcategory;

  return (
    <>
      <div className="rounded-lg border border-accent overflow-hidden">
        <div className="flex items-center gap-2 p-3 bg-muted/20">
          <button
            type="button"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={
              expanded ? 'Collapse subcategories' : 'Expand subcategories'
            }
          >
            {expanded ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
          </button>

          {editing ? (
            <Input
              value={editName}
              onChange={(event) => setEditName(event.target.value)}
              className="h-9 flex-1"
              autoFocus
            />
          ) : (
            <button
              type="button"
              className="flex-1 text-left font-medium text-sm"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {category.name}
            </button>
          )}

          <div className="flex items-center gap-1 shrink-0">
            {editing ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setEditName(category.name);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveCategory}
                  isLoading={isUpdatingCategory}
                  disabled={!editName.trim()}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-8"
                  onClick={() => setEditing(true)}
                  aria-label={`Edit ${category.name}`}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-8 text-destructive hover:text-destructive"
                  onClick={() =>
                    setDeleteTarget({
                      type: 'category',
                      id: category.id,
                      name: category.name,
                    })
                  }
                  aria-label={`Delete ${category.name}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {expanded && (
          <div className="border-t border-accent px-3 py-2">
            {isLoading ? (
              <Paragraph className="text-xs text-muted-foreground py-2">
                Loading subcategories...
              </Paragraph>
            ) : subcategories.length === 0 ? (
              <Paragraph className="text-xs text-muted-foreground py-2">
                No subcategories for this category.
              </Paragraph>
            ) : (
              <ul className="flex flex-col gap-1">
                {subcategories.map((subcategory) => (
                  <li
                    key={subcategory.id}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-2 py-1.5',
                      editingSubcategoryId === subcategory.id && 'bg-muted/40'
                    )}
                  >
                    {editingSubcategoryId === subcategory.id ? (
                      <Input
                        value={subcategoryEditName}
                        onChange={(event) =>
                          setSubcategoryEditName(event.target.value)
                        }
                        className="h-8 flex-1"
                        autoFocus
                      />
                    ) : (
                      <span className="flex-1 text-sm">{subcategory.name}</span>
                    )}

                    <div className="flex items-center gap-1 shrink-0">
                      {editingSubcategoryId === subcategory.id ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingSubcategoryId(null);
                              setSubcategoryEditName('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleSaveSubcategory(subcategory)}
                            isLoading={isUpdatingSubcategory}
                            disabled={!subcategoryEditName.trim()}
                          >
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="size-8"
                            onClick={() => {
                              setEditingSubcategoryId(subcategory.id);
                              setSubcategoryEditName(subcategory.name);
                            }}
                            aria-label={`Edit ${subcategory.name}`}
                          >
                            <Pencil className="size-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="size-8 text-destructive hover:text-destructive"
                            onClick={() =>
                              setDeleteTarget({
                                type: 'subcategory',
                                id: subcategory.id,
                                name: subcategory.name,
                                categoryId: category.id,
                              })
                            }
                            aria-label={`Delete ${subcategory.name}`}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(isOpen) => {
          if (!isOpen) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Delete{' '}
              {deleteTarget?.type === 'category' ? 'category' : 'subcategory'}?
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.type === 'category'
                ? `This will permanently delete "${deleteTarget?.name}" and all of its subcategories.`
                : `This will permanently delete the subcategory "${deleteTarget?.name}".`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              isLoading={isDeleting}
              label="Delete"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
